import { useState } from 'react';
import { Card, Avatar, Input, Space, Typography, Row, Col, Empty, Spin } from 'antd';
import { UserOutlined, SearchOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { PasscodeInputModal } from './PasscodeInputModal';
import { withDevModalFilePath } from '../common';

const { Text } = Typography;
const { Search } = Input;
const { Modal } = withDevModalFilePath('client/src/components/operation/OperatorSelectionModal.tsx');

interface OperatorSelectionModalProps {
  visible: boolean;
  sharedUserId: string;
  onClose: () => void;
  onOperatorSelected: (operatorId: string, operatorName: string) => void;
}

interface OperatorCardProps {
  id: string;
  name: string;
  nameKana?: string;
  employeeCode?: string;
  avatarUrl?: string;
  onClick: () => void;
}

/**
 * オペレーターカード（タッチ操作に最適化）
 */
function OperatorCard({ id, name, nameKana, employeeCode, avatarUrl, onClick }: OperatorCardProps) {
  return (
    <Card
      hoverable
      onClick={onClick}
      data-operator-id={id}
      style={{
        borderRadius: '12px',
        textAlign: 'center',
        height: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s',
        border: '2px solid #e8e8e8',
      }}
      styles={{
        body: {
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        },
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#1890ff';
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e8e8e8';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Space direction="vertical" align="center" size="middle" style={{ width: '100%' }}>
        <Avatar
          size={80}
          src={avatarUrl}
          icon={<UserOutlined />}
          style={{
            background: '#1890ff',
            fontSize: '32px',
          }}
        >
          {name[0]}
        </Avatar>
        <div style={{ width: '100%' }}>
          <Text
            strong
            style={{
              fontSize: 18,
              display: 'block',
              marginBottom: '4px',
            }}
          >
            {name}
          </Text>
          {nameKana && (
            <Text
              type="secondary"
              style={{
                fontSize: 14,
                display: 'block',
                marginBottom: '4px',
              }}
            >
              {nameKana}
            </Text>
          )}
          {employeeCode && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              #{employeeCode}
            </Text>
          )}
        </div>
      </Space>
    </Card>
  );
}

/**
 * オペレーター選択モーダル
 */
export function OperatorSelectionModal({
  visible,
  sharedUserId,
  onClose,
  onOperatorSelected,
}: OperatorSelectionModalProps) {
  const [searchText, setSearchText] = useState('');
  const [selectedOperator, setSelectedOperator] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [passcodeModalVisible, setPasscodeModalVisible] = useState(false);

  // オペレーター一覧取得
  const { data: operators, isLoading } = trpc.userOperator.getOperators.useQuery(
    { sharedUserId, includeInactive: false },
    { enabled: visible && !!sharedUserId }
  );

  // 検索フィルター
  const filteredOperators = operators?.filter((operator) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      operator.name.toLowerCase().includes(search) ||
      operator.nameKana?.toLowerCase().includes(search) ||
      operator.employeeCode?.toLowerCase().includes(search)
    );
  });

  const handleOperatorClick = (operatorId: string, operatorName: string) => {
    setSelectedOperator({ id: operatorId, name: operatorName });
    setPasscodeModalVisible(true);
  };

  const handlePasscodeSubmit = () => {
    if (selectedOperator) {
      onOperatorSelected(selectedOperator.id, selectedOperator.name);
      setPasscodeModalVisible(false);
      setSelectedOperator(null);
      setSearchText('');
    }
  };

  const handlePasscodeCancel = () => {
    setPasscodeModalVisible(false);
    setSelectedOperator(null);
  };

  const handleModalClose = () => {
    setSearchText('');
    setSelectedOperator(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={
          <Text style={{ fontSize: 24, fontWeight: 'bold' }}>
            オペレーターを選択してください
          </Text>
        }
        open={visible}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        style={{ top: 40 }}
        styles={{ body: { padding: '32px' } }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 検索バー */}
          <Search
            placeholder="名前、カナ、従業員コードで検索"
            prefix={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ fontSize: 18 }}
            allowClear
          />

          {/* オペレーターグリッド */}
          <div style={{ maxHeight: '600px', overflowY: 'auto', paddingRight: '8px' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <Spin size="large" />
                <Text style={{ display: 'block', marginTop: 16, fontSize: 16 }}>
                  オペレーター一覧を読み込んでいます...
                </Text>
              </div>
            ) : filteredOperators && filteredOperators.length > 0 ? (
              <Row gutter={[24, 24]}>
                {filteredOperators.map((operator) => (
                  <Col key={operator.id} xs={24} sm={12} md={8} lg={6}>
                    <OperatorCard
                      id={operator.id}
                      name={operator.name}
                      nameKana={operator.nameKana || undefined}
                      employeeCode={operator.employeeCode || undefined}
                      avatarUrl={operator.avatarUrl || undefined}
                      onClick={() => handleOperatorClick(operator.id, operator.name)}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty
                description={
                  searchText
                    ? '検索条件に一致するオペレーターが見つかりません'
                    : 'オペレーターが登録されていません'
                }
                style={{ margin: '80px 0' }}
              />
            )}
          </div>
        </Space>
      </Modal>

      {/* パスコード入力モーダル */}
      {selectedOperator && (
        <PasscodeInputModal
          visible={passcodeModalVisible}
          operator={selectedOperator}
          sharedUserId={sharedUserId}
          onSuccess={handlePasscodeSubmit}
          onCancel={handlePasscodeCancel}
        />
      )}
    </>
  );
}
